import React, { Component } from 'react';
import { addDays, startOfWeek, subDays } from 'date-fns';
import SelectReact from 'react-select';
import {
  Grid,
  Paper,
  Snackbar,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';

import WeekPicker from '../components/Schedule/WeekPicker';
import AvailabilityPositionSchedule from '../components/Availability/AvailabilityPositionSchedule';
import { formatDateOnlyStringFromLocalTime, determineEmployeeType } from '../helpers/helpers';
import {
  fetchAvailabilityByPosition,
  fetchPositions,
} from '../helpers/externalRequests';
import { formatPositionOptions,  } from '../helpers/optionFormatters';
import employeeTypeMenuItems from '../components/Employee/EmployeeTypeMenuItems'
import i18n from '../i18n';
import AvailabilityLegend from '../components/Availability/AvailabilityLegend';

class AvailabilityView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      availabilityData: [],
      filteredEmployeeType: 'none',
      hideUnavailables: true,
      startOfSelectedWeek: startOfWeek(new Date(), {
        weekStartsOn: 1,
      }),
      numberOfRequestedWeeks: 1,
      positionOptions: [], 
      selectedPositionIds: [],
      snackbarMessage: '',
      snackOpen: false,
      includeWeekend: false,
    };
  }

  componentDidMount() {
    fetchPositions()
      .then(
        response => {
          const positionOptions = formatPositionOptions(response.data);
          this.setState({
            positionOptions
          });
        }
      )
      .catch(
        error => {
          this.setState(
            {
              snackOpen: true,
              snackbarMessage: `${i18n.t('availability:positionFetchError')} ${error}`,
            }
          );
        }
      );
  }

  hideSnackbar = () => {
    this.setState({ snackOpen: false });
  };

  handleEmployeeTypeSelect = event => {
    const filteredEmployeeType = event.target.value;
    this.setState({
      filteredEmployeeType,
    }, () => {
      this.refreshPositionTables();
    })
  };

  handleHideUnavailables = () => {
    this.setState(prevState => ({
      hideUnavailables: !prevState.hideUnavailables,
    }), () => this.refreshPositionTables());
  };

  handleSelectedPositionsUpdate = arrayOfSelectedItems => {
    const {
      selectedPositionIds
    } = this.state;
    const updatedSelectedPositionIds = arrayOfSelectedItems
      ? arrayOfSelectedItems.map(selectedItem => selectedItem.value)
      : [];
    updatedSelectedPositionIds.forEach(updatedId => {
      if(!selectedPositionIds.includes(updatedId)) {
        this.getAvailabilityData(updatedId);
      }
    });
    this.setState({
      selectedPositionIds: updatedSelectedPositionIds
    });
  }

  getAvailabilityData = (positionId) => {
    const {
      numberOfRequestedWeeks,
      startOfSelectedWeek,
    } = this.state;
    const startDate = formatDateOnlyStringFromLocalTime(startOfSelectedWeek);
    const endDate = formatDateOnlyStringFromLocalTime(
      addDays(startOfSelectedWeek, numberOfRequestedWeeks * 7 - 1)
    );
    fetchAvailabilityByPosition(positionId, startDate, endDate)
      .then((response) => {
        this.setState(prevState => {
          const availabilityData =  [...prevState.availabilityData];
           
          availabilityData[response.data.positionId] = this.filterEmployeeType(response.data.scheduleData);
          return {
            availabilityData,
          }
        });
      })
      .catch((error) => {
        this.setState(
          {
            snackOpen: true,
            snackbarMessage: `${i18n.t('availability:availabilityByPositionFetchError')} ${error}`,
          }
        );
      });
  };

  filterEmployeeType = (scheduleData) => {
    const { filteredEmployeeType } = this.state;
    if (!filteredEmployeeType || filteredEmployeeType === 'none') { 
      return scheduleData;
    }
    return scheduleData.filter((employee) => {
      const employeeType = determineEmployeeType(employee.isFullTime, employee.isContractor);   
      return filteredEmployeeType === employeeType;
    });
  }

  refreshPositionTables = () => {
    const {
      selectedPositionIds
    } = this.state;
    selectedPositionIds.forEach(positionId =>
      this.getAvailabilityData(positionId)
    );
  }

  displayPositionTables = () => {
    const {
      availabilityData,
      includeWeekend,
      numberOfRequestedWeeks,
      positionOptions,
      selectedPositionIds,
      startOfSelectedWeek,
      hideUnavailables,
    } = this.state;
    const positionTables = [];
    selectedPositionIds.forEach(id => {
      positionTables.push(
        <AvailabilityPositionSchedule
          key={`positionSchedule-${id}`}
          includeWeekend={includeWeekend}
          numberOfRequestedWeeks={numberOfRequestedWeeks}
          positionName={positionOptions.find(option => option.value === id)?.name}
          scheduleData={availabilityData[id]}
          startOfSelectedWeek={startOfSelectedWeek}
          hideUnavailables={hideUnavailables}
        />
      )
    });
    
    return positionTables.length === 0 ? (
      <Paper className="blockContainer">
        <p>{i18n.t('availability:noPositionSelected')}</p>
      </Paper>
    ) : (
      positionTables
    );
  }

  onSelectNextWeek = () => {
    const {
      startOfSelectedWeek
    } = this.state;
    const updatedStartOfSelectedWeek = startOfWeek(
      addDays(startOfSelectedWeek, 7),
      { weekStartsOn: 1 }
    );
    this.setState({
      startOfSelectedWeek: updatedStartOfSelectedWeek,
    }, () => this.refreshPositionTables())
  }

  onSelectPreviousWeek = () => {
    const {
      startOfSelectedWeek
    } = this.state;
    const updatedStartOfSelectedWeek = startOfWeek(
      subDays(startOfSelectedWeek, 7),
      { weekStartsOn: 1 }
    );
    this.setState({
      startOfSelectedWeek: updatedStartOfSelectedWeek,
    }, () => this.refreshPositionTables())
  }

  onWeekChange = (dateSelected) => {
    const updatedStartOfSelectedWeek = startOfWeek(dateSelected, {
      weekStartsOn: 1,
    });

    this.setState({
      startOfSelectedWeek: updatedStartOfSelectedWeek,
    }, () => {
      this.refreshPositionTables();
    })
  }

  onWeekendClick = () => {
    this.setState(prevstate => ({
      includeWeekend: !prevstate.includeWeekend,
    }));
  }

  onSelectMultipleWeeks = (numberOfRequestedWeeks) => {
    this.setState(
      { numberOfRequestedWeeks: numberOfRequestedWeeks.value },
      () => this.refreshPositionTables()
    );
  }
 
  render() {
    const {
      includeWeekend,
      filteredEmployeeType,
      hideUnavailables,
      numberOfRequestedWeeks,
      positionOptions,
      selectedPositionIds,
      snackbarMessage,
      snackOpen,
      startOfSelectedWeek
    } = this.state;

    return (
      <>
        <Grid container className='scheduleView'>
          <Grid item xs={3} className='sectionContainer'>
            <WeekPicker
              startOfSelectedWeek={startOfSelectedWeek}
              onChange={this.onWeekChange}
              onSelectPreviousWeek={this.onSelectPreviousWeek}
              onSelectNextWeek={this.onSelectNextWeek}
              includeWeekend={includeWeekend}
              onWeekendClick={this.onWeekendClick}
              onSelectMultipleWeeks={this.onSelectMultipleWeeks}
              multipleWeekValue={numberOfRequestedWeeks}
            />
            <Paper className='blockContainer'>
              <Grid container>
                <Grid item xs={12}>
                  <h4>{i18n.t('availability:selectPositions')}</h4>
                </Grid>
                <Grid item xs={12}>
                  <SelectReact
                    fullwidth
                    isMulti
                    hideSelectedOptions
                    value={positionOptions.filter(
                      position => selectedPositionIds.includes(position.id)
                    )}
                    options={positionOptions}
                    onChange={this.handleSelectedPositionsUpdate}
                  />
                </Grid>
              </Grid>
            </Paper>
            <Paper className='blockContainer'>
              <Grid container>
                <Grid item xs={12}>
                  <h4>{i18n.t('availability:selectEmployeeType')}</h4>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label={i18n.t('filterEmployeeType')}
                    value={filteredEmployeeType}
                    onChange={this.handleEmployeeTypeSelect}
                    helperText={i18n.t('employeeTypeFilterHelp')}
                  >
                    <MenuItem value="none">{i18n.t('none')}</MenuItem>
                    {employeeTypeMenuItems}
                  </TextField>
                </Grid>
              </Grid>         
            </Paper>
            <Paper className='blockContainer'>
              <Grid container>
                <Grid item xs={12}>
                  <FormControlLabel
                    className="blackLabel"
                    control={
                      <Checkbox
                        checked={hideUnavailables}
                        onChange={this.handleHideUnavailables}
                      />
                    }
                    label={i18n.t('showAvailableEmployees')}
                  />
                </Grid>
              </Grid>         
            </Paper>    
            <AvailabilityLegend />
          </Grid>
          <Grid item xs={9} className='sectionContainer positionsContainer'>
            {this.displayPositionTables()}
          </Grid>
        </Grid>
        <Snackbar
          message={snackbarMessage}
          open={snackOpen}
          onClose={this.hideSnackbar}
          autoHideDuration={2000}
        />
      </>
    )
  }
}

export default AvailabilityView;
